import React, { useState } from "react";
import {
  Container,
  Card,
  TextField,
  Button,
  CssBaseline,
  Checkbox
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import { ErrorMessage, Field, Form, Formik } from "formik";
import Swal from "sweetalert2";
import LoadingOverlay from "react-loading-overlay-ts";
import BeatLoader from "react-spinners/BeatLoader";
import { registerUser } from "../services/registerService";
import { registrationSchema, registerInitialValues } from "../helpers/validationSchemas";
import TitleBox from "../components/TitleBox";
import TurnstileWrapped  from "../components/TurnstyleWrapped";

function RegisterPage() {
  const [recaptchaCompleted, setRecaptchaCompleted] = useState<boolean>(false);
  const [checked, setChecked] = React.useState(true);
  const [isLoading, setLoading] = useState<boolean>(false);

  const validate = (isSubmitting: boolean, verificationComplete: boolean, errors: any) => {
    let valid = false;
    if (isSubmitting || !verificationComplete || Object.keys(errors).length > 0) {
      valid = true;
    }
    return valid;
  };

  const handleSubmit = async (values: any) => {
    if (recaptchaCompleted) {
      setLoading(true);
      await registerUser({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        allowNotifications: checked,
        organization: values.organization,
        jobRole: values.jobRole,
        serialNumber: values.serialNumber.trim(),
        
      }).then((res) => {
          setLoading(false);
          Swal.fire({
            width: "30vw",
            icon: res.status || "success",
            title: res.status === "warning" ? "Registration Pending" : "Registration Complete",
            text:
              res.message ||
              "Thank you for registering, please check your email for further instructions."
          });
      }).catch((err: any) => {
        setLoading(false);
        Swal.fire({
          width: "30vw",
          icon: "error",
          title: err.response.data.message
        });
      });
    }
  };

  const handleChange = (isChecked: boolean) => {
    setChecked(!isChecked);
  };

  return (
    <Container maxWidth="sm">
      <LoadingOverlay active={isLoading} spinner={<BeatLoader color="#F4B43C" />}>
        <CssBaseline />

        <TitleBox title="Account Registration" />

        <Card
          sx={{
            padding: 2,
            textAlign: "center",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0
          }}
        >
          <Formik
            initialValues={registerInitialValues}
            validationSchema={registrationSchema}
            onSubmit={() => {}}
            initialTouched={{
              field: true
            }}
            validateOnMount
          >
            {({ isSubmitting, resetForm, values, errors, validateForm }) => (
              <Form>
                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  autoFocus
                  className="input-field"
                />

                <ErrorMessage
                  name="firstName"
                  component="div"
                  className="error-message"
                  id="first-name-error"
                />

                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  className="input-field"
                />

                <ErrorMessage
                  name="lastName"
                  component="div"
                  className="error-message"
                  id="last-name-error"
                />

                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  required
                  fullWidth
                  id="email"
                  label="Email Address"
                  name="email"
                  type="email"
                  className="input-field"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="error-message"
                  id="email-error"
                />

                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  required
                  fullWidth
                  id="organization"
                  label="Organization"
                  name="organization"
                  className="input-field"
                />
                <ErrorMessage
                  name="organization"
                  component="div"
                  className="error-message"
                  id="organization-error"
                />
                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  fullWidth
                  id="jobRole"
                  label="Job Role"
                  name="jobRole"
                  className="input-field"
                />

                <Field
                  as={TextField}
                  variant="outlined"
                  margin="normal"
                  size="small"
                  fullWidth
                  id="serialNumber"
                  label="Serial Number"
                  name="serialNumber"
                  placeholder="Serial Number e.g: V2-2222-2222-2222-2222-2222-2222"
                  className="input-field"
                />
                <ErrorMessage
                  name="serialNumber"
                  component="div"
                  className="error-message"
                  id="serialNumber-error"
                  />
                
                <TurnstileWrapped 
                  action="register"
                  onChanged={(isValid)=>{
                    if(isValid){
                      setRecaptchaCompleted(true);
                    } else {
                      setRecaptchaCompleted(false);
                    }
                  }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      defaultChecked
                      checked={checked}
                      onChange={() => {
                        handleChange(checked);
                      }}
                    />
                  }
                  label="Subscribe to service status updates from OLI Systems, Inc."
                />

                <Button
                  id="submit-button"
                  onClick={() => {
                    validateForm(values).then((formErrors) => {
                      if (Object.keys(formErrors).length === 0) {
                        resetForm();
                        handleSubmit(values);
                      } else {
                        Swal.fire({
                          icon: "warning",
                          title: "There were errors in your submission.",
                          text: `Please check the form and try again. ${Object.values(formErrors)}`
                        });
                      }
                    });
                  }}
                  fullWidth
                  sx={{
                    backgroundColor: "#F5B43B",
                    color: "black",
                    fontSize: "14px",
                    fontWeight: "bold",
                    paddingBottom: "10px"
                  }}
                  variant="contained"
                  className="submit-button"
                  disabled={validate(isSubmitting, recaptchaCompleted, errors)}
                >
                  Submit
                </Button>
                <div className="contact-us" id="contact-us">
                  <a target="_blank" rel="noreferrer" href="https://www.olisystems.com/contact-us/">
                    Not a current OLI Customer?
                  </a>
                </div>
              </Form>
            )}
          </Formik>
        </Card>
      </LoadingOverlay>
    </Container>
  );
}

export default RegisterPage;
